import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Container,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Lock from '@mui/icons-material/Lock';
import Person from '@mui/icons-material/Person';
import CheckCircle from '@mui/icons-material/CheckCircle';

import { authService } from '../services/authService';

export default function Signup({ onNavigate }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const tempErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = (formData.username || '').replace(/[-+() ]/g, '');
    const isPhone = /^\d{10}$/.test(phoneDigits);

    if (!formData.name.trim()) {
      tempErrors.name = 'Full name is required';
    }

    if (!formData.username) {
      tempErrors.username = 'Username is required';
    } else if (!emailRegex.test(formData.username) && !isPhone) {
      tempErrors.username = 'Please enter a valid email address or mobile number';
    }

    if (!formData.password) {
      tempErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      tempErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      tempErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.password) {
      tempErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setIsSuccess(false);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const response = await authService.signup(formData.username, formData.password, formData.name);
      setIsSuccess(true);
      setSuccessMsg(response.message || 'Signup successful!');
      setTimeout(() => {
        setLoading(false);
        if (onNavigate) {
          onNavigate('login');
        } else {
          window.location.href = '/login';
        }
      }, 1200);
    } catch (error) {
      setLoading(false);
      setIsSuccess(false);
      setErrorMsg(error.message || 'Signup failed. Please try again.');
    }
  };

  const logoMark = (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'conic-gradient(from 200deg, #D4A24C, #E4572E, #2F8F76, #D4A24C)',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        mr: 1.5,
        '&::after': {
          content: '""',
          width: 16,
          height: 16,
          backgroundColor: '#0F2E2B',
          borderRadius: '50%',
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '16px 12px', sm: '24px 16px', md: '48px 24px' },
        position: 'relative',
        background: '#0F2E2B',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(600px 400px at 85% -10%, rgba(212,162,76,0.14), transparent 60%),
            radial-gradient(500px 400px at -10% 80%, rgba(47,143,118,0.18), transparent 60%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, px: { xs: 1, sm: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(21, 58, 53, 0.6)',
            backdropFilter: 'blur(20px)',
            filter: loading ? 'blur(6px)' : 'none',
            transition: 'filter 0.3s ease',
            border: '1px solid rgba(245, 241, 230, 0.12)',
            borderRadius: { xs: '20px', sm: '24px' },
            padding: { xs: 2.5, sm: 3.5, md: 4.5 },
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            pointerEvents: loading ? 'none' : 'auto',
          }}
        >
          {/* Logo & Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 2, sm: 3 } }}>
            {logoMark}
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: '#F5F1E6',
                letterSpacing: '-0.01em',
                fontSize: { xs: '24px', sm: '32px' },
              }}
            >
              iSoret
            </Typography>
          </Box>

          <Typography
            variant="h5"
            align="center"
            sx={{
              fontFamily: '"Fraunces", serif',
              color: '#F5F1E6',
              fontWeight: 600,
              fontSize: { xs: '20px', sm: '24px' },
              mb: 0.5,
            }}
          >
            Sign Up
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{ color: 'rgba(245, 241, 230, 0.66)', mb: { xs: 2.5, sm: 3.5 } }}
          >
            Create an account to start claiming premium deals.
          </Typography>

          {successMsg && (
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                p: 2,
                borderRadius: '8px',
                backgroundColor: 'rgba(47, 143, 118, 0.15)',
                border: '1px solid #2F8F76',
              }}
            >
              <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                {successMsg}
              </Typography>
            </Box>
          )}

          {errorMsg && (
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                p: 2,
                borderRadius: '8px',
                backgroundColor: 'rgba(228, 87, 46, 0.15)',
                border: '1px solid #E4572E',
              }}
            >
              <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                {errorMsg}
              </Typography>
            </Box>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                placeholder="John Doe"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'rgba(245, 241, 230, 0.4)' }} />
                      </InputAdornment>
                    ),
                  }
                }}
                sx={inputStyles}
              />

              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                error={!!errors.username}
                helperText={errors.username || 'Enter your email or mobile number'}
                placeholder="Email or mobile number"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'rgba(245, 241, 230, 0.4)' }} />
                      </InputAdornment>
                    ),
                  }
                }}
                sx={inputStyles}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                error={!!errors.password}
                helperText={errors.password}
                placeholder="••••••••"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'rgba(245, 241, 230, 0.4)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: 'rgba(245, 241, 230, 0.7)',
                            '&:hover': { color: '#D4A24C' }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
                sx={inputStyles}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                placeholder="••••••••"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'rgba(245, 241, 230, 0.4)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{
                            color: 'rgba(245, 241, 230, 0.7)',
                            '&:hover': { color: '#D4A24C' }
                          }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
                sx={inputStyles}
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 1.5, sm: 2 } }}>
                <Button
                  type="submit"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.2, sm: 1.5 },
                    px: { xs: 4, sm: 6 },
                    minWidth: { xs: '160px', sm: '200px' },
                    fontSize: { xs: '15px', sm: '16px' },
                    backgroundColor: '#D4A24C',
                    color: '#0F2E2B',
                    '&:hover': {
                      backgroundColor: '#E8C078',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(212, 162, 76, 0.3)',
                      color: 'rgba(15, 46, 43, 0.5)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
                </Button>
              </Box>
            </Box>
          </form>

          {/* Link to Login */}
          <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.66)' }}>
              Already have an account?
            </Typography>
            <Button
              onClick={() => onNavigate && onNavigate('login')}
              sx={{
                color: '#D4A24C',
                textTransform: 'none',
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Log In
            </Button>
          </Box>

        </Paper>
      </Container>

      {/* Full-screen Backdrop Blur Overlay during signup & profile creation */}
      <Backdrop
        open={loading}
        sx={{
          color: '#D4A24C',
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(15, 46, 43, 0.65)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          p: 3,
        }}
      >
        {isSuccess ? (
          <CheckCircle sx={{ fontSize: 60, color: '#4CAF50' }} />
        ) : (
          <CircularProgress size={56} thickness={4} sx={{ color: '#D4A24C' }} />
        )}

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              color: '#F5F1E6',
              fontWeight: 700,
              fontFamily: '"Fraunces", serif',
              letterSpacing: '0.02em',
              mb: 0.8,
            }}
          >
            {isSuccess ? 'User Profile Created!' : 'Creating user profile...'}
          </Typography>
          <Typography
            sx={{
              color: isSuccess ? '#4CAF50' : 'rgba(245, 241, 230, 0.7)',
              fontSize: '14.5px',
              fontWeight: isSuccess ? 600 : 400,
            }}
          >
            {isSuccess
              ? 'Redirecting to login page...'
              : 'Please wait while we set up your account'}
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
}

const inputStyles = {
  '& .MuiInputLabel-root': {
    color: 'rgba(245, 241, 230, 0.5)',
    '&.Mui-focused': {
      color: '#D4A24C',
    },
    '&.Mui-error': {
      color: '#E4572E',
    },
  },
  '& .MuiOutlinedInput-root': {
    color: '#F5F1E6',
    backgroundColor: 'rgba(15, 46, 43, 0.4)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(245, 241, 230, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(245, 241, 230, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#D4A24C',
      borderWidth: '1.5px',
    },
    '&.Mui-error fieldset': {
      borderColor: '#E4572E',
    },
  },
  '& .MuiFormHelperText-root': {
    color: 'rgba(245, 241, 230, 0.5)',
    '&.Mui-error': {
      color: '#E4572E',
      marginLeft: '4px',
    },
  },
};
